// valet-keychain-qr — qr-label-pdf: pure geometry/pagination helpers for the
// QR label sheet PDF. Kept dependency-free (no react-pdf import) so these can
// be unit-tested without the render/SVG engine and reused by both the PDF
// document (QrLabelSheetPdf.tsx) and the on-screen preview (LabelGridPreview.tsx).

/** 1mm in PDF points (react-pdf's native unit), same conversion printers use. */
export const MM_TO_PT = 2.834645669;

// D3 — sync client-side PDF generation is capped per-PDF at 250 labels,
// stricter than the 500-code batch-creation cap (Phase 1). A single 500-code
// batch is exported as multiple PDF files (see chunkCodesForPdf / export-qr-labels.tsx).
export const MAX_LABELS_PER_PDF = 250;

// Minimum label edge (mm) below which a QR code cannot stay scannable at
// typical handheld-scanner read distance. Configs below this floor are
// clamped up, never silently rendered illegibly small.
export const MIN_LABEL_SIZE_MM = 20;

// Minimum QR *code* edge (mm), independent of the label's own size — always
// enforced even if the caller shrinks the label toward MIN_LABEL_SIZE_MM.
export const MIN_QR_SIZE_MM = 15;

// High error-correction floor (~30% recoverable) — required regardless of
// configured label size (qr-label-pdf / minimum scannable QR quality).
export const QR_ERROR_CORRECTION_LEVEL = 'H';

/**
 * What a label's QR encodes. Single source of truth shared by the PDF export
 * and the on-screen preview so the two can never drift apart.
 */
export function qrPayloadForCode(code: string): string {
  return code;
}

/** Render resolution for the QR PNG fed into react-pdf's <Image>, px per mm. */
const QR_PX_PER_MM = 8;

export type PageSizeId = 'A4' | 'LETTER';

export const PAGE_SIZES_MM: Record<PageSizeId, { width: number; height: number }> = {
  A4: { width: 210, height: 297 },
  LETTER: { width: 215.9, height: 279.4 },
};

export interface LabelGridConfig {
  pageSize: PageSizeId;
  /** Labels per row. */
  columns: number;
  /** Labels per column. */
  rows: number;
  labelWidthMm: number;
  labelHeightMm: number;
  /** Uniform page margin (mm) — no per-side config, matches the simple admin form. */
  marginMm: number;
}

export interface ClampedLabelGridConfig {
  config: LabelGridConfig;
  /** True when one or more dimensions were raised to the minimum scannable floor. */
  clamped: boolean;
}

/**
 * Enforces the minimum scannable label floor (qr-label-pdf requirement).
 * A config at/above the floor passes through unchanged; anything smaller is
 * clamped UP to MIN_LABEL_SIZE_MM rather than rejected outright, so the admin
 * never hits a dead-end form — the preview (LabelGridPreview) reflects the
 * clamped value immediately.
 */
export function clampLabelGridConfig(input: LabelGridConfig): ClampedLabelGridConfig {
  const labelWidthMm = Math.max(input.labelWidthMm, MIN_LABEL_SIZE_MM);
  const labelHeightMm = Math.max(input.labelHeightMm, MIN_LABEL_SIZE_MM);
  const clamped = labelWidthMm !== input.labelWidthMm || labelHeightMm !== input.labelHeightMm;
  return { config: { ...input, labelWidthMm, labelHeightMm }, clamped };
}

/**
 * Groups a flat item list into pages of `columns*rows` items, preserving
 * order. Generic over T so it drives both the PDF document (QrLabelItem[])
 * and the pure-string preview/tests.
 */
export function paginateLabels<T>(items: T[], columns: number, rows: number): T[][] {
  const perPage = Math.max(1, Math.floor(columns)) * Math.max(1, Math.floor(rows));
  if (items.length === 0) return [];
  const pages: T[][] = [];
  for (let i = 0; i < items.length; i += perPage) {
    pages.push(items.slice(i, i + perPage));
  }
  return pages;
}

/**
 * Splits a code list into PDF-sized chunks (MAX_LABELS_PER_PDF each). A
 * reprint of a full 500-code batch produces multiple downloaded PDF files
 * rather than rejecting the export outright (D3).
 */
export function chunkCodesForPdf(codes: string[], maxPerPdf = MAX_LABELS_PER_PDF): string[][] {
  if (codes.length === 0) return [];
  const chunks: string[][] = [];
  for (let i = 0; i < codes.length; i += maxPerPdf) {
    chunks.push(codes.slice(i, i + maxPerPdf));
  }
  return chunks;
}

/**
 * Number of separate PDF files a given code count will produce. Kept here
 * (not in export-qr-labels.tsx) so callers that only need the count — e.g.
 * QrGeneratorPage's "this will download as N files" note — don't have to
 * statically import the @react-pdf/renderer + qrcode chunk just to read it.
 */
export function exportPartCount(codeCount: number, maxPerPdf = MAX_LABELS_PER_PDF): number {
  return Math.max(1, Math.ceil(codeCount / maxPerPdf));
}

/**
 * Pixel width/height to request from `qrcode.toDataURL` for a given label
 * config — scales with the label's shorter edge but never drops below the
 * MIN_QR_SIZE_MM floor (converted to px via QR_PX_PER_MM).
 */
export function qrPixelSizeForLabel(config: LabelGridConfig): number {
  const shortSideMm = Math.min(config.labelWidthMm, config.labelHeightMm);
  const qrSideMm = Math.max(shortSideMm * 0.7, MIN_QR_SIZE_MM);
  return Math.round(qrSideMm * QR_PX_PER_MM);
}
